import { axiosSSO, refreshTokenTimeouts } from 'config';
import { refreshToken } from 'services/authService';
import store from '../store/store';
import { authActions } from '../store/authSlice/authSlice';
import { snackbarActions } from '../store/snackbarsSlice/snackbarsSlice';

const ignoreUnauthorizedErrorPaths = ['/login', '/logout'];
const isUnauthorized = (error) => error.response?.status === 401;
const shouldIgnoreUrl = (url) => ignoreUnauthorizedErrorPaths.some((path) => url?.endsWith?.(path));
const initiateLogout = (error) => isUnauthorized(error) && !shouldIgnoreUrl(error.config?.url);

let isRefreshing = false;
let pendingQueue = [];

const flushQueue = (error) => {
    const queue = pendingQueue;
    pendingQueue = [];
    queue.forEach(({ resolve, reject, request }) => {
        if (error) {
            reject(error);
        } else {
            resolve(axiosSSO.request(request));
        }
    });
};

const createTimeoutError = (timeoutMs) => {
    const timeoutError = new Error(`Refresh timeout after ${timeoutMs}ms`);
    timeoutError.isTimeout = true;
    return timeoutError;
};

const performRefreshWithTimeout = (timeoutMs) => {
    const refreshPromise = refreshToken();
    let timeoutId;

    const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(createTimeoutError(timeoutMs)), timeoutMs);
    });

    return Promise.race([refreshPromise.finally(() => clearTimeout(timeoutId)), timeoutPromise]);
};

export const handleResponseError = (error) => {
    if (initiateLogout(error)) store.dispatch(authActions.logoutStart());
    return Promise.reject(error);
};

export async function handleResponseInterceptorError(error) {
    const status = error?.response?.status;
    const originalRequest = error?.config;
    const url = originalRequest?.url;

    if (status !== 401 || shouldIgnoreUrl?.(url)) {
        return Promise.reject(error);
    }

    if (url === '/auth/refresh' || originalRequest?._retry) {
        store.dispatch(authActions.logoutStart());
        return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
        return new Promise((resolve, reject) => {
            pendingQueue.push({ resolve, reject, request: originalRequest });
        });
    }

    isRefreshing = true;
    try {
        const attemptRefresh = async () => {
            try {
                return await performRefreshWithTimeout(refreshTokenTimeouts.initial);
            } catch (firstError) {
                if (firstError?.isTimeout) {
                    store.dispatch(
                        snackbarActions.enqueueSnackbar({
                            message: 'Trying to reconnect, please wait…',
                            options: { variant: 'info' },
                        })
                    );
                    const result = await performRefreshWithTimeout(refreshTokenTimeouts.retry);
                    store.dispatch(
                        snackbarActions.enqueueSnackbar({
                            message: 'Reconnected successfully',
                            options: { variant: 'success' },
                        })
                    );
                    return result;
                }
                throw firstError;
            }
        };

        const refreshResponse = await attemptRefresh();
        if (refreshResponse?.status === 200) {
            const retried = axiosSSO.request(originalRequest);
            isRefreshing = false;
            flushQueue(null);
            return retried;
        }
        store.dispatch(authActions.logoutStart());
        flushQueue(new Error('Refresh failed'));
        return Promise.reject(error);
    } catch (refreshErr) {
        store.dispatch(authActions.logoutStart());
        flushQueue(refreshErr);
        return Promise.reject(refreshErr);
    } finally {
        isRefreshing = false;
    }
}
